import useNotify from 'hooks/useNotify';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CreatePromoCodePayload, PromoCodeType } from 'models/PromoCode';
import useAppUser from 'hooks/useAppUser';

import Loader from 'components/loader/Loader';
import PageHeader from 'components/pageHeader/PageHeader';
import FormWithProvider from 'components/forms/form/Form';
import useCreatePromoCode from 'components/domain/promoCode/useCreatePromoCode';
import { format } from 'date-fns';

import useFetchAllPropertiesForPromoCodes from '../useFetchAllPropertiesForPromoCodes';
import { Container } from './AddPromoCode.styles';
import AddForm from './addForm/AddForm';
import addPromoCodeSchema, {
  AddPromoCodeFormValues,
} from './addForm/AddForm.schema';

const AddPromoCode = () => {
  const { notifyError } = useNotify();
  const { agency } = useAppUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isPropertiesLoading, properties } =
    useFetchAllPropertiesForPromoCodes();

  const { createPromoCode, isLoading: isCreatingPromoCode } =
    useCreatePromoCode();

  const handleSubmit = (values: AddPromoCodeFormValues) => {
    const payload: CreatePromoCodePayload = {
      ...values,
      value: Number(values.value),
      validFromDate: `${format(values.validFromDate, 'yyyy-MM-dd')}T00:00:00`,
      validToDate: `${format(values.validToDate, 'yyyy-MM-dd')}T23:59:59`,
      expirationDate: `${format(values.expirationDate, 'yyyy-MM-dd')}T23:59:59`,
      currency: agency!.currency,
    };

    createPromoCode(payload)
      .then(() => {
        navigate('/promo-codes');
      })
      .catch(() => {
        notifyError(
          t('pageDiscountCodes.pageAddDiscount.errorCreatingPromoCode'),
        );
      });
  };

  if (isPropertiesLoading) {
    return <Loader fullPage />;
  }

  return (
    <>
      {isCreatingPromoCode && <Loader fullPage overlay />}
      <Container>
        <PageHeader
          title={t('pageDiscountCodes.pageAddDiscount.addDiscountCodeHeader')}
        />
        <FormWithProvider
          schema={addPromoCodeSchema}
          defaultValues={{
            type: PromoCodeType.AMOUNT,
            propertyUid: '',
            value: 0,
          }}
          horizontal
          onSubmit={handleSubmit}
        >
          <AddForm properties={properties ?? []} />
        </FormWithProvider>
      </Container>
    </>
  );
};

export default AddPromoCode;
